import {
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { AdobeLaunchService } from '../../../../services/adobe-launch/adobe-launch.service';
import { ContentUtil } from '../../../../utils/content/content.util';
import {
    CheckboxComponent,
    CheckboxInputComponent,
    CheckboxState,
} from '@vg-constellation/angular-20/checkbox';
import { LinkComponent } from '@vg-constellation/angular-20/link';
import { IconComponent } from '@vg-constellation/angular-20/icon';
import { BannerComponent } from '@vg-constellation/angular-20/banner';
import { ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { BehaviorSubject, combineLatest, Observable, Subscription, take } from 'rxjs';
import { OwnAccountDTO } from '../../../../../common/dtos/own-account.dto';
import { IneligibleReasons } from '../../../../../common/enums/ineligible-scenarios.enum';
import { selectOwnAccounts, selectUserInfo } from '../../../../store/selectors/account-permissions.selector';
import {
    selectRemoveUserStepperSelectedUser,
    selectRemoveUserStepperSelectedAccounts,
    selectRemoveUserStepperSelectedUserName,
    selectRemoveUserStepperSelectedAccessLevelLabel,
} from '../../../../store/selectors/grant-revoke-permissions.selector';
import { updateRemoveUserStepperSelectedAccounts } from '../../../../store/actions/remove-user-stepper-flow.action';
import { environment } from '../../../../../environments/environment';
import { LinkTarget } from '../../../../enums/link-target.enum';
import { ReplaceParamPipe } from '../../../../pipes/replace-param/replace-param.pipe';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './raf-select-accounts-step-adobe';
import { LoggerService } from '../../../../services/logger/logger.service';

interface AccountGroup {
    eligible: OwnAccountDTO[];
    requireForm: OwnAccountDTO[];
    otherIneligible: OwnAccountDTO[];
    temporarilyIneligible: OwnAccountDTO[];
}

@AdobeTagging(adobeConfigData)
@Component({
    selector: 'zci-raf-select-accounts-step',
    standalone: true,
    imports: [
        CommonModule,
        CheckboxComponent,
        CheckboxInputComponent,
        LinkComponent,
        IconComponent,
        BannerComponent,
        ModalDialogModule,
        ReplaceParamPipe,
    ],
    templateUrl: './raf-select-accounts-step.component.html',
    styleUrls: ['./raf-select-accounts-step.component.scss'],
})
export class RafSelectAccountsStepComponent implements OnInit, OnDestroy {
    @ViewChild('selectionError') selectionErrorElement?: ElementRef<HTMLDivElement>;
    @Input() nextStepReady: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() locationChanged: EventEmitter<any> = new EventEmitter<any>();
    grantorContent = ContentUtil.grantorFragmentContent;
    granteeName$: Observable<string>;
    accessLevelLabel$: Observable<string>;
    selectedAccountIds: Set<string> = new Set();
    eligibleAccountCount = 0;
    showSelectionError = false;
    isLoading = true;
    protected adobeLaunchService = inject(AdobeLaunchService);
    private readonly store = inject(Store);
    private readonly loggerService = inject(LoggerService);
    private accountGroupsSubject = new BehaviorSubject<AccountGroup>({
        eligible: [],
        requireForm: [],
        otherIneligible: [],
        temporarilyIneligible: [],
    });
    accountGroups$ = this.accountGroupsSubject.asObservable();
    private subs: Subscription = new Subscription();
    private scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

    get allAccountsSelected(): boolean {
        return (
            this.eligibleAccountCount > 0 &&
            this.selectedAccountIds.size === this.eligibleAccountCount
        );
    }

    ngOnInit(): void {
        this.granteeName$ = this.store.select(selectRemoveUserStepperSelectedUserName);
        this.accessLevelLabel$ = this.store.select(selectRemoveUserStepperSelectedAccessLevelLabel);

        this.subs.add(
            this.accountGroups$.subscribe((groups) => {
                this.eligibleAccountCount = groups.eligible.length;
            }),
        );

        this.subs.add(
            this.nextStepClicked?.subscribe(() => {
                this.onContinue();
            }),
        );

        this.loadAccounts();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        if (this.scrollTimeoutId) {
            clearTimeout(this.scrollTimeoutId);
        }
    }

    onAccountChange(state: CheckboxState, account: OwnAccountDTO): void {
        if (!account.accountId) return;

        if (state.isChecked) {
            this.selectedAccountIds.add(account.accountId);
            this.showSelectionError = false;
        } else {
            this.selectedAccountIds.delete(account.accountId);
        }
    }

    toggleSelectAll(): void {
        if (this.allAccountsSelected) {
            this.store.select(selectUserInfo).pipe(take(1)).subscribe((userInfo) => {
                this.loggerService.info(`toggleSelectAll: deselecting all accounts (grantorPoid: ${userInfo?.clientPoid})`);
                this.selectedAccountIds.clear();
            });
        } else {
            combineLatest([this.accountGroups$, this.store.select(selectUserInfo)])
                .pipe(take(1))
                .subscribe(([groups, userInfo]) => {
                    groups.eligible.forEach((account) => {
                        if (account.accountId) {
                            this.selectedAccountIds.add(account.accountId);
                        }
                    });
                    this.loggerService.info(`toggleSelectAll: selecting all ${this.selectedAccountIds.size} eligible accounts (grantorPoid: ${userInfo?.clientPoid})`);
                    this.showSelectionError = false;
                });
        }
    }

    isAccountSelected(account: OwnAccountDTO): boolean {
        return !!account.accountId && this.selectedAccountIds.has(account.accountId);
    }

    onContinue(): void {
        if (this.selectedAccountIds.size === 0) {
            if (this.eligibleAccountCount > 0) {
                this.showSelectionError = true;
                this.scrollTimeoutId = setTimeout(() => {
                    this.selectionErrorElement?.nativeElement?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }, 0);
                this.nextStepReady?.emit(false);
                return;
            }
            this.store.dispatch(updateRemoveUserStepperSelectedAccounts({ selectedAccounts: [] }));
            this.nextStepReady?.emit(true);
            return;
        }

        this.accountGroups$.pipe(take(1)).subscribe((groups) => {
            const selectedAccounts = groups.eligible.filter((account) =>
                account.accountId ? this.selectedAccountIds.has(account.accountId) : false,
            );
            this.store.dispatch(updateRemoveUserStepperSelectedAccounts({ selectedAccounts }));
            this.nextStepReady?.emit(true);
        });
    }

    onBack(): void {
        this.locationChanged?.emit(-1);
    }

    navigateToForm(): void {
        window.open(environment.AGENT_AUTHORIZATION_FORM_URL, LinkTarget.Self);
    }

    private loadAccounts(): void {
        combineLatest([
            this.store.select(selectOwnAccounts),
            this.store.select(selectRemoveUserStepperSelectedUser),
            this.store.select(selectRemoveUserStepperSelectedAccounts),
        ])
            .pipe(take(1))
            .subscribe(([accounts, selectedUser, previouslySelectedAccounts]) => {
                const granteePoid = selectedUser?.clientPoid;
                if (!accounts?.length || !granteePoid) {
                    this.isLoading = false;
                    return;
                }

                this.groupAccounts(accounts, granteePoid);

                if (previouslySelectedAccounts?.length) {
                    previouslySelectedAccounts.forEach((account) => {
                        if (account.accountId) {
                            this.selectedAccountIds.add(account.accountId);
                        }
                    });
                }

                this.isLoading = false;
            });
    }

    private groupAccounts(accounts: OwnAccountDTO[], granteePoid: string): void {
        const groups: AccountGroup = {
            eligible: [],
            requireForm: [],
            otherIneligible: [],
            temporarilyIneligible: [],
        };

        accounts.forEach((account) => {
            if (!account.accountId) return;

            const granteeHasAccess = account.grantees?.some((g) => g.clientPoid === granteePoid);
            if (!granteeHasAccess) return;

            const isFormRequired = this.accountRequiresForm(account.isIneligible ?? []);
            const isTemporarilyIneligible = this.isTemporarilyIneligible(account.isIneligible ?? []);

            if (isFormRequired) {
                groups.requireForm.push(account);
            } else if (isTemporarilyIneligible) {
                groups.temporarilyIneligible.push(account);
            } else {
                groups.eligible.push(account);
            }
        });

        this.accountGroupsSubject.next(groups);
    }

    private accountRequiresForm(ineligibleReasons: IneligibleReasons[]): boolean {
        const formRequiringReasons = [IneligibleReasons.TRUST_OR_ORGANIZATION_ACCOUNT];
        return ineligibleReasons.some((reason) => formRequiringReasons.includes(reason));
    }

    private isTemporarilyIneligible(ineligibleReasons: IneligibleReasons[]): boolean {
        return ineligibleReasons.includes(IneligibleReasons.TECH_DIFF);
    }
}
